import React from "react";
import CompanyLayout from "@/components/CompanyLayout";
import { Button } from "@/components/ui/button";
import { 
  ArrowRight, Shield, Mail
} from "lucide-react";

const SecurityPolicy = () => {
  const pageTitle = "Security";
  const pageDescription = "Learn about Assembly's security practices and what we're doing to protect your data.";

  return (
    <CompanyLayout title={pageTitle} description={pageDescription}>
      {/* Hero Section - Dark background like original */}
      <section className="bg-gray-900 py-16 md:py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">Security</h1>
            <p className="text-lg text-gray-300 mb-4">Last updated on April 29, 2022</p>
            <div className="flex items-center justify-center gap-2 mb-8">
              <span className="text-lg text-white">Assembly's SOC 2 Type II compliance is monitored by</span>
              <span className="font-bold text-assembly-blue">Vanta</span>
              <Shield className="h-5 w-5 text-assembly-blue" />
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Security</h2>
            <p className="text-gray-700 leading-relaxed mb-8">
              We often get questions from our users about our security practices and what we're doing to protect their data. 
              And though we don't want to reveal too much of what we do behind the curtain, we want to lay out some of the most 
              important things we do to protect your data and also what you can do to protect your own data when using Assembly.
            </p>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12">What we do to protect your data:</h2>
            
            <div className="space-y-8">
              {/* Data Handling */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Data Handling</h3>
                <p className="text-gray-700 leading-relaxed">
                  We take handling your data very seriously. We classify all data, and our employees are trained on proper handling of your (and our) data. Our employees are granted access to systems that hold your data on a "need-to-know" basis (i.e. if required to perform their job). Employees who have access to systems that hold your data are required to use strong passwords and multi-factor authentication.
                </p>
              </div>

              {/* Data Encryption */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Data Encryption</h3>
                <p className="text-gray-700 leading-relaxed">
                  We encrypt all communication between you and our applications using industry standard SSL/TLS encryption. Cloud Database provider encrypts all cluster storage and snapshot volumes, securing all cluster data on disk: a concept known as encryption at rest. We hash all passwords and have no way to decrypt them so if you forget your password, resetting it is the only option. We store all your data in ISO 27001 compliant data centers in the United States.
                </p>
              </div>

              {/* Credit Card Safety */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Credit Card Safety</h3>
                <p className="text-gray-700 leading-relaxed">
                  When you purchase a paid subscription with Assembly, we neither store nor transmit your credit card information. We use{" "}
                  <a href="https://stripe.com/docs/security/stripe" target="_blank" rel="noopener noreferrer" className="text-assembly-blue hover:underline font-semibold">
                    Stripe
                  </a>
                  , a PCI-DSS Level 1 compliant payment processor to handle all credit card transactions.
                </p>
              </div>

              {/* We Do The Right Thing */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">We Do The Right Thing</h3>
                <p className="text-gray-700 leading-relaxed">
                  One of our core values is that <strong>we do the right thing</strong>. We embody this by keeping our technical stack, our application, and our business processes lean and free of unnecessary complexity. We automate as much testing, deployment and backup processes as possible to reduce any human error. All new code is seen by at least two pairs of eyes and evaluated against our secure coding standards. We regularly tear out code that has reached the end of its usefulness to keep our application simple, elegant, and secure.
                </p>
              </div>

              {/* We Get Stuff Done and Go Beyond */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">We Get Stuff Done and Go Beyond</h3>
                <p className="text-gray-700 leading-relaxed">
                  Another of our core values is that we get stuff done and go beyond. All of our employees receive regular security and data handling training to be made aware of common and new security threats and how to mitigate them. Our engineering staff are constantly evaluating and integrating new technologies into our stack and application to create the best possible user experience and to increase security.
                </p>
              </div>

              {/* Monitoring */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Monitoring</h3>
                <p className="text-gray-700 leading-relaxed">
                  We actively monitor security issues and releases of our technical stack and deploy patches as quickly as possible. We utilize multiple types of logging to monitor the live (and past) state of our application to help detect and recover from any security events. We maintain a list of our vendors' security policies and monitor our vendors for security breaches that could lead back to our application.
                </p>
              </div>

              {/* We do more */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">We do more</h3>
                <p className="text-gray-700 leading-relaxed">
                  This is not a comprehensive list of the security measures we keep to safeguard your data. If you have any more questions please contact us, we're glad to answer any and all of your questions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What You Can Do Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12">What you can do to protect your data:</h2>
            
            <div className="space-y-8">
              {/* Multi-Factor Authentication */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Use Multi-factor Authentication (or SSO)</h3>
                <p className="text-gray-700 leading-relaxed">
                  Our application allows you and your colleagues to enable multi-factor authentication, which helps prevent unauthorized access. *If you already have a single sign-on at your organization (e.g. Okta, Azure SSO) we can provide integration to most SAML providers, which means you wouldn't need to remember another password.
                </p>
              </div>

              {/* Manage Users Automatically */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Manage Users Automatically</h3>
                <p className="text-gray-700 leading-relaxed">
                  Manually adding and removing users and permissions can often be overlooked and are a common source of unauthorized access to data (i.e. it can be easy to forget to remove an employee from Assembly when they leave your organization). To prevent this, we recommend automatically managing users. Assembly provides integrations with ADP and Slack. Users can alternatively be managed by any IdP that supports the SCIM 2.0 protocol (Okta, Azure, OneLogin, G-Suite).
                </p>
              </div>

              {/* Security Audits */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Security Audits</h3>
                <p className="text-gray-700 leading-relaxed">
                  **Our application keeps security logs of user access (user logins and IPs) and many other events (e.g. changes to groups, changes of reviewers, etc…) which can be audited via audit log requests to{" "}
                  <a href="mailto:support@joinassembly.com" className="text-assembly-blue hover:underline font-semibold">
                    support@joinassembly.com
                  </a>
                  .
                </p>
              </div>

              {/* Learn about privacy settings */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Learn about privacy settings</h3>
                <p className="text-gray-700 leading-relaxed">
                  Different companies have different demands as to privacy settings. Assembly provides many options when it comes to privacy, but this means that you need to ensure you understand and are using our application in a way consistent with your requirements. For any privacy concerns, you can take a look at our privacy policy here. Feel free to email us for any additional questions at{" "}
                  <a href="mailto:support@joinassembly.com" className="text-assembly-blue hover:underline font-semibold">
                    support@joinassembly.com
                  </a>
                  .
                </p>
              </div>

              {/* Reporting security issues */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Reporting security issues</h3>
                <p className="text-gray-700 leading-relaxed">
                  If you believe you've found something in Assembly that has security implications, please email them to follow our{" "}
                  <a href="https://joinassembly.com/vulnerability-disclosure-policy" target="_blank" rel="noopener noreferrer" className="text-assembly-blue hover:underline font-semibold">
                    Responsible Disclosure Policy
                  </a>
                  .
                </p>
              </div>
            </div>

            <div className="mt-12 p-6 bg-blue-50 rounded-xl">
              <p className="text-sm text-gray-600 mb-2">* SSO is included with some plans or can be purchased as an add-on for others.</p>
              <p className="text-sm text-gray-600">** Security events are logged for all accounts, but these are only accessible on some plans.</p>
            </div>
          </div>
        </div>
      </section>

    </CompanyLayout>
  );
};

export default SecurityPolicy;